<template>
  <div class="flex min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
    <Notification
      v-if="showNotification"
      :type="notification.type"
      :title="notification.title"
      :message="notification.message"
      :duration="5000"
    />
    <ForgotPasswordModal
      :show="showForgotPasswordModal"
      @close="showForgotPasswordModal = false"
    />
    <!-- Colonne de gauche avec le logo -->
    <div class="hidden lg:flex lg:w-1/2 relative bg-gray">
      <div class="absolute inset-0 flex items-center justify-end mr-10">
        <img
          src="/images/logo-agco-v2.svg"
          alt="Logo Lisaprint"
          class="w-[300px] h-auto"
        >
      </div>
      <div class="absolute right-0 top-0 bottom-0 w-px bg-gray-200" />
    </div>

    <!-- Colonne de droite avec le formulaire -->
    <div class="flex-1 flex items-center justify-start p-8">
      <div class="w-full max-w-md">
        <div class="lg:hidden mb-8 text-center">
          <img
            src="/images/logo-lisaprint.svg"
            alt="Logo Lisaprint"
            class="w-[200px] h-auto mx-auto mb-6"
          >
          <h1 class="text-2xl font-['Roboto_Black'] text-gray-900 mb-2">
            Bienvenue sur Lisaprint
          </h1>
          <p class="text-gray-600 font-['Roboto_Regular']">
            Votre plateforme de gestion de documents personnalisés
          </p>
        </div>

        <div
          class="bg-white rounded-2xl shadow-xl p-8 backdrop-blur-lg bg-white/80"
        >
          <h2
            class="text-2xl font-['Roboto_Black'] text-gray-900 mb-8 text-center"
          >
            Je me connecte
          </h2>

          <form class="space-y-6" autocomplete="off" @submit.prevent="handleSubmit">
            <div class="space-y-2">
              <label
                for="username"
                class="block text-sm font-['Roboto_Medium'] text-gray-700"
              >
                Compte AGCO
              </label>
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                >
                  <svg
                    class="h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  id="username"
                  v-model="form.username"
                  type="text"
                  required
                  class="block w-full pl-10 pr-4 py-3 rounded-xl border-gray-300 shadow-sm focus:border-[#B70018] focus:ring-[#B70018] sm:text-sm font-['Roboto_Regular'] transition-all duration-200"
                  placeholder="Votre identifiant"
                  autocomplete="off"
                >
              </div>
            </div>

            <div class="space-y-2">
              <label
                for="password"
                class="block text-sm font-['Roboto_Medium'] text-gray-700"
              >
                Mot de passe
              </label>
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                >
                  <svg
                    class="h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  id="password"
                  v-model="form.password"
                  type="password"
                  required
                  class="block w-full pl-10 pr-4 py-3 rounded-xl border-gray-300 shadow-sm focus:border-[#B70018] focus:ring-[#B70018] sm:text-sm font-['Roboto_Regular'] transition-all duration-200"
                  placeholder="Votre mot de passe"
                  autocomplete="new-password"
                >
              </div>
            </div>

            <div
              v-if="error"
              class="p-4 rounded-xl bg-red-50 border border-red-200"
            >
              <p
                class="text-sm text-[#B70018] text-center font-['Roboto_Regular']"
              >
                {{ error }}
              </p>
            </div>

            <div class="flex items-center justify-between">
              <div class="text-sm">
                <button
                  type="button"
                  class="font-['Roboto_Medium'] text-[#B70018] hover:text-[#B70018]/80 transition-colors duration-200"
                  @click="handleForgotPassword"
                >
                  Identifiants perdus ?
                </button>
              </div>
            </div>

            <button
              type="submit"
              :disabled="loading"
              class="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-['Roboto_Medium'] text-white bg-[#B70018] hover:bg-[#B70018]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B70018] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <span v-if="loading" class="flex items-center">
                <svg
                  class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Connexion...
              </span>
              <span v-else>Se connecter</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "@vue/runtime-core";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { clearCache } from "@/utils/cache";
import Notification from "@/components/ui/Notification.vue";
import ForgotPasswordModal from "@/components/dialogs/ForgotPasswordModal.vue";

interface NotificationState {
  type: "success" | "error" | "warning";
  title: string;
  message: string;
}

interface FormState {
  username: string;
  password: string;
}

const route = useRoute();
const authStore = useAuthStore();

const form = ref<FormState>({
  username: "",
  password: "",
});

const loading = ref<boolean>(false);
const error = ref<string>("");
const showNotification = ref(false);
const notification = ref<NotificationState>({
  type: "success",
  title: "",
  message: "",
});

const showForgotPasswordModal = ref<boolean>(false);

// Fonction pour gérer la connexion directe via token temporaire
const handleDirectLogin = async (): Promise<void> => {
  const authToken = route.query.auth_token as string;
  const uuid = route.query.uuid as string;

  if (authToken && uuid) {
    try {
      loading.value = true;
      error.value = "";

      // 1. Purger tout résidu de session/cache d'un utilisateur précédent AVANT
      //    de se connecter, pour qu'aucune donnée ne fuie d'un user à l'autre.
      //    On garde un éventuel auth_token déjà présent uniquement pour pouvoir
      //    être écrasé par le nouveau dans loginWithTempToken.
      localStorage.removeItem("user_data");
      localStorage.removeItem("brand");
      localStorage.removeItem("user_logo");
      localStorage.removeItem("user_signatures");
      localStorage.removeItem("redirect_after_brand_change");
      localStorage.removeItem("last_brand_change_time");
      localStorage.removeItem("brand_assets_cache");
      localStorage.removeItem("cache_timestamp");
      localStorage.removeItem("cached_providers");
      localStorage.removeItem("catalogs_cache");
      localStorage.removeItem("documents_cache");
      localStorage.removeItem("helps");
      localStorage.removeItem("operation_cache");
      localStorage.removeItem("agco_documents_cache");
      sessionStorage.clear();
      await clearCache();

      // 2. Connexion avec le token temporaire (place le nouveau auth_token + user_data)
      await authStore.loginWithTempToken(uuid, authToken);

      // 3. Notification
      const userData = authStore.user;
      const userName = userData?.user_details?.name || userData?.email || "Utilisateur";
      const userRoles = userData?.roles || [];
      const isSupplier = userRoles.includes('ROLE_SUPPLIER');
      const userBrand = userData?.groups?.[0]?.name || 'AGCO';

      notification.value = {
        type: "success",
        title: "Switch User réussi",
        message: `Vous êtes maintenant connecté en tant que ${userName} (${userBrand}${isSupplier ? ' - Fournisseur' : ''})`,
      };
      showNotification.value = true;

      await new Promise(resolve => setTimeout(resolve, 1500));

      // 4. Hard reload pour réinitialiser tous les stores Pinia avec le nouveau user
      window.location.href = "/";
    } catch (err: unknown) {
      console.error("Erreur de connexion directe:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Une erreur est survenue lors de la connexion";
      error.value = errorMessage;

      notification.value = {
        type: "error",
        title: "Erreur de connexion",
        message: errorMessage,
      };
      showNotification.value = true;
    } finally {
      loading.value = false;
    }
  }
};

onMounted(() => {
  void handleDirectLogin();
});

const handleSubmit = async (): Promise<void> => {
  try {
    loading.value = true;
    error.value = "";

    if (!form.value.username || !form.value.password) {
      error.value = "Veuillez remplir tous les champs";
      return;
    }

    await authStore.login(form.value.username, form.value.password);
  } catch (err: unknown) {
    console.error("Erreur de connexion:", err);
    const errorMessage =
      err instanceof Error
        ? err.message
        : "Une erreur est survenue lors de la connexion";
    error.value = errorMessage;
  } finally {
    loading.value = false;
  }
};

const handleForgotPassword = (): void => {
  showForgotPasswordModal.value = true;
};
</script>
